import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "tabButton",
    "tabPanel",
    "recipientSelector",
    "audienceTab",
    "allStudentsOption",
    "zoneValue",
    "classroomValue",
    "studentProgramValue",
    "pupilValue",
    "studentProgramStatusPanel",
    "makeupStudentsPanel",
    "postcodeFilterPanel",
    "recipientError",
    "additionalFilters",
    "additionalFiltersToggle",
    "enrolledPreset",
    "enrolledCustomRange",
    "enrolledAfter",
    "enrolledBefore",
    "dobPreset",
    "dobCustomRange",
    "dobAfter",
    "dobBefore"
  ]

  connect() {
    this.syncRecipientSelector()
    this.toggleDependentPanels()
    this.loadPanelFrameForCurrentTab()
    this.syncDatePresetsFromFields()
  }

  selectTab(event) {
    event.preventDefault()
    this.showTab(event.currentTarget.dataset.tab)
  }

  showTab(tab) {
    this.tabButtonTargets.forEach((button) => {
      button.classList.toggle("is-active", button.dataset.tab === tab)
    })

    if (this.hasAudienceTabTarget) {
      this.audienceTabTarget.value = this.currentTab()
    }

    this.tabPanelTargets.forEach((panel) => {
      const show = panel.dataset.tab === tab
      panel.classList.toggle("u-hidden", !show)
      if (show) this.loadPanelFrame(panel)
    })

    this.syncRecipientSelector()
    this.toggleDependentPanels()
    this.clearError()
  }

  loadPanelFrameForCurrentTab() {
    const tab = this.currentTab()
    const panel = this.tabPanelTargets.find((item) => item.dataset.tab === tab)
    if (panel) this.loadPanelFrame(panel)
  }

  loadPanelFrame(panel) {
    const frame = panel.querySelector("turbo-frame")
    if (!frame) return
    const src = frame.dataset.src
    if (!src || frame.getAttribute("src")) return
    frame.setAttribute("src", src)
  }

  onSelectorChanged() {
    this.syncRecipientSelector()
    this.toggleDependentPanels()
    this.clearError()
  }

  updateInstructorSelection() {
    this.onSelectorChanged()
  }

  toggleAdditionalFilters(event) {
    event.preventDefault()
    if (!this.hasAdditionalFiltersTarget) return

    const isHidden = this.additionalFiltersTarget.classList.toggle("u-hidden")
    if (this.hasAdditionalFiltersToggleTarget) {
      this.additionalFiltersToggleTarget.textContent = isHidden ? "Show additional filters" : "Hide additional filters"
      this.additionalFiltersToggleTarget.setAttribute("aria-expanded", (!isHidden).toString())
    }
  }

  selectDatePreset(event) {
    event.preventDefault()
    this.applyDatePreset(event.currentTarget.dataset.presetGroup, event.currentTarget.dataset.preset)
  }

  markCustomDateRange(event) {
    const group = event.currentTarget === this.enrolledAfterTarget || event.currentTarget === this.enrolledBeforeTarget
      ? "enrolled"
      : "dob"
    this.applyDatePreset(group, "custom")
  }

  syncDatePresetsFromFields() {
    if (this.hasEnrolledAfterTarget) {
      this.applyDatePreset("enrolled", this.inferPreset(this.enrolledAfterTarget.value, this.enrolledBeforeTarget.value), { skipFill: true })
    }
    if (this.hasDobAfterTarget) {
      this.applyDatePreset("dob", this.inferPreset(this.dobAfterTarget.value, this.dobBeforeTarget.value), { skipFill: true })
    }
  }

  applyDatePreset(group, preset, options = {}) {
    const fields = this.dateFieldsFor(group)
    if (!fields) return

    if (!options.skipFill && preset !== "custom") {
      const dates = this.datePresets()[preset] || this.datePresets().all_time
      fields.after.value = dates.after
      fields.before.value = dates.before
    }

    if (fields.customRange) {
      fields.customRange.classList.toggle("u-hidden", preset !== "custom")
    }

    fields.buttons.forEach((button) => {
      const selected = button.dataset.preset === preset
      button.classList.toggle("Button--outline", !selected)
      button.setAttribute("aria-pressed", selected.toString())
    })
  }

  inferPreset(after, before) {
    const normalizedAfter = after || ""
    const normalizedBefore = before || ""
    if (!normalizedAfter && !normalizedBefore) return "all_time"

    const last30 = this.datePresets().last_30
    if (normalizedAfter === last30.after && normalizedBefore === last30.before) return "last_30"

    const thisYear = this.datePresets().this_year
    if (normalizedAfter === thisYear.after && !normalizedBefore) return "this_year"

    return "custom"
  }

  dateFieldsFor(group) {
    if (group === "enrolled" && this.hasEnrolledAfterTarget) {
      return {
        after: this.enrolledAfterTarget,
        before: this.enrolledBeforeTarget,
        customRange: this.hasEnrolledCustomRangeTarget ? this.enrolledCustomRangeTarget : null,
        buttons: this.hasEnrolledPresetTarget ? this.enrolledPresetTargets : []
      }
    }
    if (group === "dob" && this.hasDobAfterTarget) {
      return {
        after: this.dobAfterTarget,
        before: this.dobBeforeTarget,
        customRange: this.hasDobCustomRangeTarget ? this.dobCustomRangeTarget : null,
        buttons: this.hasDobPresetTarget ? this.dobPresetTargets : []
      }
    }
    return null
  }

  datePresets() {
    const today = this.startOfToday()
    const last30 = new Date(today)
    last30.setDate(last30.getDate() - 30)
    const yearStart = new Date(today.getFullYear(), 0, 1)

    return {
      all_time: { after: "", before: "" },
      last_30: { after: this.toISODate(last30), before: this.toISODate(today) },
      this_year: { after: this.toISODate(yearStart), before: "" }
    }
  }

  startOfToday() {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return today
  }

  toISODate(date) {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const day = String(date.getDate()).padStart(2, "0")
    return `${year}-${month}-${day}`
  }

  syncRecipientSelector() {
    if (!this.hasRecipientSelectorTarget) return

    const tab = this.currentTab()
    let value = ""

    if (tab === "all_students") {
      const selected = this.allStudentsOptionTargets.find((option) => option.checked)
      value = selected ? selected.value : ""
    } else if (tab === "zones") {
      value = this.normalizedValue(this.hasZoneValueTarget ? this.zoneValueTarget.value : "")
    } else if (tab === "classrooms") {
      value = this.normalizedValue(this.hasClassroomValueTarget ? this.classroomValueTarget.value : "")
    } else if (tab === "student_programs") {
      value = this.normalizedValue(this.hasStudentProgramValueTarget ? this.studentProgramValueTarget.value : "")
    } else if (tab === "individual") {
      const pupilId = this.normalizedValue(this.hasPupilValueTarget ? this.pupilValueTarget.value : "")
      value = pupilId ? `pupil_${pupilId}` : ""
    }

    this.recipientSelectorTarget.value = value
  }

  toggleDependentPanels() {
    const tab = this.currentTab()
    const recipientId = this.hasRecipientSelectorTarget ? this.recipientSelectorTarget.value : ""

    if (this.hasStudentProgramStatusPanelTarget) {
      const show = tab === "student_programs" && String(recipientId).startsWith("student_program_")
      this.studentProgramStatusPanelTarget.classList.toggle("u-hidden", !show)
    }

    if (this.hasMakeupStudentsPanelTarget) {
      const show = tab === "classrooms" && String(recipientId).startsWith("program_")
      this.makeupStudentsPanelTarget.classList.toggle("u-hidden", !show)
    }

    if (this.hasPostcodeFilterPanelTarget) {
      this.postcodeFilterPanelTarget.classList.toggle("u-hidden", tab === "individual")
    }
  }

  validateSubmit(event) {
    this.syncRecipientSelector()
    const tab = this.currentTab()
    const value = this.hasRecipientSelectorTarget ? this.recipientSelectorTarget.value : ""
    const needsSelection = ["zones", "classrooms", "student_programs", "individual"]

    if (needsSelection.includes(tab) && !value) {
      event.preventDefault()
      this.showError(this.selectionErrorFor(tab))
    }
  }

  currentTab() {
    const activeButton = this.tabButtonTargets.find((button) => button.classList.contains("is-active"))
    return activeButton?.dataset.tab || "all_students"
  }

  normalizedValue(value) {
    if (value == null) return ""
    const trimmed = String(value).trim()
    if (!trimmed || trimmed === "0") return ""
    return trimmed
  }

  selectionErrorFor(tab) {
    switch (tab) {
      case "zones":
        return "Please select a zone."
      case "classrooms":
        return "Please select a classroom."
      case "student_programs":
        return "Please select a student program."
      case "individual":
        return "Please select a student."
      default:
        return "Please select who to send this message to."
    }
  }

  showError(message) {
    if (!this.hasRecipientErrorTarget) return
    this.recipientErrorTarget.textContent = message
    this.recipientErrorTarget.classList.remove("u-hidden")
  }

  clearError() {
    if (!this.hasRecipientErrorTarget) return
    this.recipientErrorTarget.textContent = ""
    this.recipientErrorTarget.classList.add("u-hidden")
  }
}
